import { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    X, ChevronLeft, ChevronRight, Network, Mail, Zap, Users,
    Target, Lightbulb, CalendarDays, Sparkles, Info, Building2,
    CheckCircle2, Loader2
} from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import { OUTREACH_CONTEXTS } from '../../constants/outreachStrategies';

const PATH_ICONS = { Network, Mail, Zap };

/**
 * "Choose Your Outreach Strategy" flow.
 *
 * Three views, all in one lightweight modal:
 *   paths  → pick Internal Referral vs Direct / Hiring-Signal (user's choice)
 *   list   → pick a sub-strategy within the chosen path
 *   detail → who to contact + 4-step action plan + "why this works"
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {'job'|'signal'} context
 * @param {string} companyName
 * @param {string} [jobTitle]
 * @param {Array}  [contacts]  used for the per-path "who to contact" chips
 */
const OutreachStrategyModal = ({ open, onClose, context = 'job', companyName = 'this company', jobTitle, contacts = [], onActivate, onViewTasks }) => {
    const ctx = OUTREACH_CONTEXTS[context] || OUTREACH_CONTEXTS.job;

    const [view, setView] = useState('paths');
    const [pathId, setPathId] = useState(null);
    const [strategyKey, setStrategyKey] = useState(null);

    // Reset to the first view whenever the modal is (re)opened.
    // When a context offers a single path, skip the chooser and open it directly.
    const singlePath = ctx.paths.length === 1;
    useEffect(() => {
        if (!open) return;
        setStrategyKey(null);
        if (singlePath) { setPathId(ctx.paths[0].id); setView('list'); }
        else { setPathId(null); setView('paths'); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // ESC closes; lock background scroll while open.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
    }, [open, onClose]);

    const currentPath = useMemo(() => ctx.paths.find((p) => p.id === pathId) || null, [ctx, pathId]);
    const currentStrategy = useMemo(
        () => currentPath?.subStrategies.find((s) => s.key === strategyKey) || null,
        [currentPath, strategyKey]
    );

    const openPath = useCallback((id) => { setPathId(id); setView('list'); }, []);
    const openStrategy = useCallback((key) => { setStrategyKey(key); setView('detail'); }, []);
    const back = useCallback(() => {
        if (view === 'detail') { setView('list'); return; }
        if (view === 'list') {
            if (singlePath) onClose?.();   // no chooser to return to
            else setView('paths');
        }
    }, [view, singlePath, onClose]);

    if (!open) return null;

    const companyChip = companyName ? (
        <span className="inline-flex items-center gap-1 max-w-full text-[11.5px] font-semibold text-[#475569] bg-[#F1F3F7] px-2 py-0.5 rounded-full">
            <Building2 className="w-3 h-3 shrink-0" />
            <span className="truncate">{companyName}</span>
        </span>
    ) : null;

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

            <div className="relative w-full max-w-2xl max-h-[88vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#EFF1F5] shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        {view !== 'paths' && (
                            <button onClick={back} aria-label="Back"
                                className="w-8 h-8 grid place-items-center rounded-lg text-[#64748B] hover:bg-[#F1F3F7] transition-colors shrink-0">
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                        )}
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-[#3F57E0] shrink-0" />
                                <h2 className="text-[15px] font-extrabold text-[#0F172A] truncate">
                                    {view === 'paths' ? ctx.heading : (view === 'list' ? currentPath?.title : currentStrategy?.name)}
                                </h2>
                                {view === 'detail' && currentStrategy?.whatItIs && (
                                    <Tooltip title={currentStrategy.whatItIs} arrow placement="bottom"
                                        componentsProps={{ tooltip: { sx: { fontSize: 11.5, lineHeight: 1.5, maxWidth: 300, p: 1.25 } } }}>
                                        <span className="inline-flex cursor-help shrink-0">
                                            <Info className="w-4 h-4 text-[#94A3B8] hover:text-[#3F57E0] transition-colors" />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                            {/* When there's a role (job page), keep role + company on one line. */}
                            {jobTitle && (
                                <div className="flex items-center gap-2 flex-wrap mt-1">
                                    <span className="text-[12px] text-[#64748B] truncate">{jobTitle}</span>
                                    {companyChip}
                                </div>
                            )}
                            {view === 'list' && currentPath?.tagline && (
                                <p className="text-[11.5px] text-[#94A3B8] mt-1 leading-snug">{currentPath.tagline}</p>
                            )}
                            {/* No role (hiring signal): show the company below the tagline. */}
                            {!jobTitle && companyChip && (
                                <div className="mt-1.5">{companyChip}</div>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close"
                        className="w-8 h-8 grid place-items-center rounded-lg text-[#64748B] hover:bg-[#F1F3F7] transition-colors shrink-0">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto px-5 py-5">
                    {view === 'paths' && (
                        <PathChooser paths={ctx.paths} onPick={openPath} />
                    )}
                    {view === 'list' && currentPath && (
                        <StrategyList path={currentPath} onPick={openStrategy} />
                    )}
                    {view === 'detail' && currentPath && currentStrategy && (
                        <StrategyDetail path={currentPath} strategy={currentStrategy} contacts={contacts} onActivate={onActivate} onViewTasks={onViewTasks} />
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

// ── View 1: choose a path ────────────────────────────────────────────────────
const PathChooser = ({ paths, onPick }) => (
    <div className="flex flex-col gap-3">
        <p className="text-[12.5px] text-[#475569] leading-relaxed">
            Pick the path that fits your goal. Build trust with an internal employee to earn a referral, or reach the
            hiring team directly with a tailored proof-of-fit message. Each path is a focused action plan designed to get a response.
        </p>
        {paths.map((p) => {
            const Icon = PATH_ICONS[p.icon] || Network;
            return (
                <button key={p.id} onClick={() => onPick(p.id)}
                    className="group text-left rounded-2xl border border-[#E6E8EE] hover:border-[#3F57E0] hover:shadow-sm transition-all p-4 flex items-start gap-3.5">
                    <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: p.accentBg }}>
                        <Icon className="w-5 h-5" style={{ color: p.accent }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[14px] font-bold text-[#0F172A]">{p.title}</span>
                            <span className="text-[10px] font-semibold text-[#64748B] bg-[#F1F3F7] px-2 py-0.5 rounded-full">{p.cadenceNote}</span>
                        </div>
                        <p className="text-[12.5px] text-[#475569] mt-1 leading-snug">{p.tagline}</p>
                        <p className="text-[11.5px] text-[#94A3B8] mt-1.5">Best for: {p.bestFor}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#CBD5E1] group-hover:text-[#3F57E0] transition-colors shrink-0 mt-3" />
                </button>
            );
        })}
    </div>
);

// ── View 2: choose a sub-strategy ────────────────────────────────────────────
const StrategyList = ({ path, onPick }) => (
    <div className="flex flex-col gap-2.5">
        <div className="rounded-xl p-3 flex items-start gap-2.5" style={{ background: path.accentBg }}>
            <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" style={{ color: path.accent }} />
            <p className="text-[12px] leading-snug" style={{ color: '#475569' }}>{path.why}</p>
        </div>
        {path.subStrategies.map((s, i) => (
            <button key={s.key} onClick={() => onPick(s.key)}
                className="group text-left rounded-xl border border-[#E6E8EE] hover:border-[#3F57E0] hover:shadow-sm transition-all px-4 py-3 flex items-center gap-3">
                <div className="w-7 h-7 rounded-full grid place-items-center text-[12px] font-bold shrink-0"
                    style={{ background: path.accentBg, color: path.accent }}>{i + 1}</div>
                <div className="flex-1 min-w-0">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="text-[13.5px] font-bold text-[#0F172A]">{s.name}</span>
                        {s.whatItIs && (
                            <Tooltip title={s.whatItIs} arrow placement="top"
                                componentsProps={{ tooltip: { sx: { fontSize: 11.5, lineHeight: 1.5, maxWidth: 280, p: 1.25 } } }}>
                                <span onClick={(e) => e.stopPropagation()} className="inline-flex cursor-help shrink-0">
                                    <Info className="w-3.5 h-3.5 text-[#94A3B8] hover:text-[#3F57E0] transition-colors" />
                                </span>
                            </Tooltip>
                        )}
                    </span>
                    <p className="text-[11.5px] text-[#64748B] mt-0.5 truncate">Who: {s.whoToContact}</p>
                    {s.synopsis && <p className="text-[11px] text-[#94A3B8] mt-0.5">{s.synopsis}</p>}
                </div>
                <ChevronRight className="w-4 h-4 text-[#CBD5E1] group-hover:text-[#3F57E0] transition-colors shrink-0" />
            </button>
        ))}
    </div>
);

// Classify a contact into an outreach category so each path shows the RIGHT
// people: referral → a peer / recent hire on the team; direct/signal → the
// hiring manager + recruiter. Hunter tags contacts with lead_category; for
// providers that don't (ContactOut/Prospeo) we fall back to title heuristics
// (predicted_role only separates recruiters, not peers vs. managers).
const RECRUITER_RE = /recruit|talent|\bhr\b|human resources|\bpeople\b|sourc|staffing|acquisition/i;
const MANAGER_RE = /\b(manager|lead|head|director|vp|vice president|chief|c[teofi]o|principal|president|founder|owner|partner|supervisor)\b/i;

const categorizeContact = (c) => {
    const explicit = c?.lead_category || c?._fullData?.lead_category;
    if (explicit === 'peer' || explicit === 'hiring_manager' || explicit === 'recruiter') return explicit;
    const title = c?.title || c?.jobTitle || '';
    if (RECRUITER_RE.test(title) || c?.predicted_role === 'recruiter') return 'recruiter';
    if (MANAGER_RE.test(title)) return 'hiring_manager';
    return 'peer';
};

// Pick the contacts relevant to a path. Referral surfaces peers first (then
// other employees), never leading with recruiters; direct/signal surfaces the
// hiring team. Falls back to the full list so a section is never empty.
const contactsForPath = (pathId, contacts = []) => {
    const byCat = { peer: [], hiring_manager: [], recruiter: [] };
    for (const c of contacts) byCat[categorizeContact(c)].push(c);
    if (pathId === 'referral') {
        // Peers only; fall back to managers (then anyone) so it's never empty.
        const list = byCat.peer.length ? byCat.peer : byCat.hiring_manager;
        return list.length ? list : contacts;
    }
    // direct / signal: the hiring team. Fall back to peers, then anyone.
    const list = [...byCat.hiring_manager, ...byCat.recruiter];
    return list.length ? list : (byCat.peer.length ? byCat.peer : contacts);
};

// ── View 3: strategy detail (who · 4-step plan · why) ────────────────────────
const StrategyDetail = ({ path, strategy, contacts, onActivate, onViewTasks }) => {
    const matchedContacts = useMemo(
        () => contactsForPath(path.id, contacts || []).slice(0, 4),
        [path.id, contacts]
    );

    // Activate-strategy button state. Reset whenever the chosen strategy changes.
    const [activateStatus, setActivateStatus] = useState('idle'); // idle | loading | done | error
    useEffect(() => { setActivateStatus('idle'); }, [path.id, strategy.key]);

    const handleActivate = async () => {
        if (!onActivate || activateStatus === 'loading' || activateStatus === 'done') return;
        setActivateStatus('loading');
        try {
            const ok = await onActivate(path.id, strategy, matchedContacts);
            setActivateStatus(ok ? 'done' : 'error');
        } catch {
            setActivateStatus('error');
        }
    };
    return (
        <div className="flex flex-col gap-4">
            {/* Who to contact */}
            <Section icon={Users} title="Who to contact" accent={path.accent}>
                <p className="text-[12.5px] text-[#475569]">{strategy.whoToContact}</p>
                {matchedContacts.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {matchedContacts.map((c, i) => (
                            <span key={i} className="text-[11px] font-medium text-[#334155] bg-[#F1F3F7] px-2 py-1 rounded-full">
                                {c.name}{c.title ? ` · ${c.title}` : ''}
                            </span>
                        ))}
                    </div>
                )}
            </Section>

            {/* Action plan */}
            <Section icon={CalendarDays} title={`Action plan · ${path.cadenceNote}`} accent={path.accent}>
                <ol className="flex flex-col gap-0">
                    {strategy.days.map((d, i) => (
                        <li key={i} className="flex gap-3">
                            <div className="flex flex-col items-center shrink-0">
                                <div className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold text-white shrink-0"
                                    style={{ background: path.accent }}>{i + 1}</div>
                                {i < strategy.days.length - 1 && <div className="w-px flex-1 my-1" style={{ background: '#E6E8EE' }} />}
                            </div>
                            <div className={`flex-1 min-w-0 ${i < strategy.days.length - 1 ? 'pb-3.5' : ''}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[12.5px] font-bold text-[#0F172A]">{d.label}</span>
                                    <span className="text-[10px] font-semibold text-[#64748B] bg-[#F1F3F7] px-1.5 py-0.5 rounded-full">{d.channel}</span>
                                </div>
                                <p className="text-[12.5px] text-[#475569] mt-1 leading-snug">{d.action}</p>
                                <p className="text-[11px] text-[#94A3B8] mt-1 flex items-center gap-1">
                                    <Target className="w-3 h-3" /> {d.goal}
                                </p>
                            </div>
                        </li>
                    ))}
                </ol>
            </Section>

            {/* Why this works */}
            <Section icon={Lightbulb} title="Why this works" accent={path.accent}>
                <p className="text-[12.5px] text-[#475569] leading-relaxed">{strategy.why}</p>
            </Section>

            {/* Activate → seed this strategy into the Daily Tasks sprint */}
            {onActivate && (
                <div className="sticky bottom-0 -mx-5 px-5 -mb-5 pb-4 mt-3 pt-3 bg-white border-t border-[#EFF1F5]">
                    <button
                        onClick={handleActivate}
                        disabled={activateStatus === 'loading' || activateStatus === 'done'}
                        className="w-full flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-[13.5px] font-bold text-white transition-all disabled:opacity-90"
                        style={{ background: activateStatus === 'done' ? '#10B981' : path.accent }}
                    >
                        {activateStatus === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
                        {activateStatus === 'done' && <CheckCircle2 className="w-4 h-4" />}
                        {(activateStatus === 'idle' || activateStatus === 'error') && <Zap className="w-4 h-4" />}
                        {activateStatus === 'loading' ? 'Activating…'
                            : activateStatus === 'done' ? 'Added to Daily Tasks'
                            : activateStatus === 'error' ? 'Try again'
                            : 'Activate strategy'}
                    </button>
                    {activateStatus === 'done' && (
                        <div className="flex flex-col items-center gap-1.5 mt-1.5">
                            <p className="text-[11px] text-[#94A3B8] text-center">
                                This plan is now in your Daily Tasks sprint.
                            </p>
                            {onViewTasks && (
                                <button
                                    onClick={onViewTasks}
                                    className="inline-flex items-center gap-1 text-[12px] font-semibold hover:underline"
                                    style={{ color: path.accent }}
                                >
                                    View Daily Tasks <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const Section = ({ icon: Icon, title, accent, children }) => (
    <div className="rounded-2xl border border-[#E6E8EE] p-4">
        <div className="flex items-center gap-2 mb-2.5">
            <Icon className="w-4 h-4" style={{ color: accent }} />
            <h3 className="text-[12.5px] font-bold text-[#0F172A] uppercase tracking-wide">{title}</h3>
        </div>
        {children}
    </div>
);

export default OutreachStrategyModal;
